const ISO_PATTERN = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d{1,3})?(Z|[+-]\d{2}:?\d{2})?)?$/;

/**
 * Checks if a string is a valid ISO 8601 timestamp string.
 *
 * This function validates strings that conform to the ISO 8601 date/time format,
 * including various levels of precision (date-only, date-time, with milliseconds)
 * and timezone information (Z for UTC or offset like +05:30).
 *
 * @param str - The string to validate
 * @returns `true` if the string is a valid ISO 8601 timestamp, `false` otherwise
 *
 * @example
 * ```typescript
 * isISOString('2024-01-15T10:30:00Z') // true
 * isISOString('2024-01-15T10:30:00.123Z') // true
 * isISOString('2024-01-15T10:30:00+05:30') // true
 * isISOString('2024-01-15') // true
 * isISOString('not a date') // false
 * isISOString('2024-13-45') // false (invalid month/day)
 * isISOString('01/15/2024') // false (not ISO format)
 * ```
 */
export const isISOString = (str: string): boolean => {
  // First check if the string matches ISO 8601 format pattern
  // Matches: YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss or with timezone/milliseconds
  if (!ISO_PATTERN.test(str)) {
    return false;
  }

  // Try to parse the string as a date
  const date = new Date(str);

  // Check if the date is valid
  if (isNaN(date.getTime())) {
    return false;
  }

  // For date-only strings or UTC strings, verify the date wasn't adjusted
  // (e.g., '2023-02-29' becomes '2023-03-01')
  if (!str.includes("T") || str.endsWith("Z")) {
    const isoString = date.toISOString();
    const originalDatePart = str.split("T")[0];
    return originalDatePart ? isoString.startsWith(originalDatePart) : false;
  }

  // For strings with timezone offsets, we can't easily verify the date part
  // since it may shift to a different day in UTC. Just ensure it parsed successfully.
  return true;
};
